import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import api from '../services/api';

function buildQuery(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach((item) => search.append(key, item));
      return;
    }
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, value);
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
}

function flattenRecord(value, prefix = '') {
  const flattened = {};
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    Object.entries(value).forEach(([key, innerValue]) => {
      const nextKey = prefix ? `${prefix}.${key}` : key;
      Object.assign(flattened, flattenRecord(innerValue, nextKey));
    });
    return flattened;
  }
  if (Array.isArray(value)) {
    flattened[prefix] = JSON.stringify(value);
    return flattened;
  }
  flattened[prefix] = value;
  return flattened;
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'No submission';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

function getMediaKind(filename, contentType = '') {
  const normalizedType = (contentType || '').toLowerCase();
  if (normalizedType.startsWith('image/')) {
    return 'image';
  }
  if (normalizedType.startsWith('video/')) {
    return 'video';
  }
  if (normalizedType.startsWith('audio/')) {
    return 'audio';
  }
  const lower = (filename || '').toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower)) {
    return 'image';
  }
  if (/\.(mp4|webm|mov|m4v|ogg)$/.test(lower)) {
    return 'video';
  }
  if (/\.(mp3|wav|m4a|aac|oga|opus)$/.test(lower)) {
    return 'audio';
  }
  return 'file';
}

async function downloadFromApi(url, filename, mimeType) {
  const response = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: mimeType });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
}

export default function AiAnnotationsPage() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [comparisonRows, setComparisonRows] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [mediaSearch, setMediaSearch] = useState('');
  const [mediaResults, setMediaResults] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState('');
  const [mediaComparison, setMediaComparison] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [overviewResp, usersResp, comparisonResp, mediaResp] = await Promise.all([
          api.get('/admin/ai/overview'),
          api.get('/admin/ai/users'),
          api.get('/admin/ai/user-comparison'),
          api.get('/admin/ai/media'),
        ]);
        setOverview(overviewResp.data);
        setUsers(usersResp.data.users || []);
        setComparisonRows(comparisonResp.data.users || []);
        setMediaResults(mediaResp.data.media || []);
        setErrorMsg('');
      } catch (error) {
        console.error(error);
        setErrorMsg(error.response?.data?.detail || 'Failed to load AI annotation admin data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchComparison = async () => {
      setSummaryLoading(true);
      try {
        const query = buildQuery({ usernames: selectedUsers });
        const response = await api.get(`/admin/ai/user-comparison${query}`);
        setComparisonRows(response.data.users || []);
        setErrorMsg('');
      } catch (error) {
        console.error(error);
        setErrorMsg(error.response?.data?.detail || 'Failed to load user comparison data.');
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchComparison();
  }, [selectedUsers]);

  const handleSearchMedia = async () => {
    setMediaLoading(true);
    try {
      const query = buildQuery({ search: mediaSearch, usernames: selectedUsers });
      const response = await api.get(`/admin/ai/media${query}`);
      setMediaResults(response.data.media || []);
      setErrorMsg('');
    } catch (error) {
      console.error(error);
      setErrorMsg(error.response?.data?.detail || 'Failed to search media.');
    } finally {
      setMediaLoading(false);
    }
  };

  const handleLoadMediaComparison = async (mediaFilename) => {
    setSelectedMedia(mediaFilename);
    setMediaLoading(true);
    try {
      const query = buildQuery({ usernames: selectedUsers });
      const response = await api.get(`/admin/ai/media-comparison/${encodeURIComponent(mediaFilename)}${query}`);
      setMediaComparison(response.data);
      setErrorMsg('');
    } catch (error) {
      console.error(error);
      setErrorMsg(error.response?.data?.detail || 'Failed to load media comparison.');
    } finally {
      setMediaLoading(false);
    }
  };

  const handleDownloadSummary = async (format) => {
    const query = buildQuery({ usernames: selectedUsers, format });
    await downloadFromApi(
      `/admin/ai/exports/summary${query}`,
      `ai-annotation-summary.${format === 'json' ? 'json' : 'csv'}`,
      format === 'json' ? 'application/json' : 'text/csv'
    );
  };

  const handleDownloadRaw = async (format) => {
    const query = buildQuery({
      usernames: selectedUsers,
      media_filename: selectedMedia,
      format,
    });
    await downloadFromApi(
      `/admin/ai/exports/raw${query}`,
      `ai-annotation-raw${selectedMedia ? `-${selectedMedia}` : ''}.${format === 'json' ? 'json' : 'csv'}`,
      format === 'json' ? 'application/json' : 'text/csv'
    );
  };

  const mediaComparisonFields = useMemo(() => {
    if (!mediaComparison?.records?.length) {
      return [];
    }
    const fieldSet = new Set(['status', 'active_media', 'completed_media_count']);
    mediaComparison.records.forEach((record) => {
      const flattened = flattenRecord(record.annotation || {});
      Object.keys(flattened).forEach((key) => fieldSet.add(key));
    });
    return Array.from(fieldSet);
  }, [mediaComparison]);

  const mediaComparisonMap = useMemo(() => {
    const map = {};
    (mediaComparison?.records || []).forEach((record) => {
      map[record.username] = {
        status: record.status,
        active_media: record.active_media,
        completed_media_count: record.completed_media_count,
        ...flattenRecord(record.annotation || {}),
      };
    });
    return map;
  }, [mediaComparison]);

  const previewInfo = mediaComparison?.preview || null;
  const mediaUrl = previewInfo?.url ? `${api.defaults.baseURL}${previewInfo.url}` : '';
  const mediaKind = getMediaKind(previewInfo?.resolved_filename || selectedMedia, previewInfo?.content_type);
  const [mediaPreviewError, setMediaPreviewError] = useState(false);

  useEffect(() => {
    setMediaPreviewError(false);
  }, [selectedMedia]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" my={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack spacing={3}>
        <Card>
          <CardHeader title="AI Annotation Admin" />
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              Track AI annotation submissions, compare annotators across users, and download summary or raw outputs.
            </Typography>
          </CardContent>
        </Card>

        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <Card><CardContent><Typography variant="overline">Total Users</Typography><Typography variant="h5">{overview?.total_users ?? 0}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card><CardContent><Typography variant="overline">With Submissions</Typography><Typography variant="h5">{overview?.users_with_submissions ?? 0}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card><CardContent><Typography variant="overline">Active Media</Typography><Typography variant="h5">{overview?.users_with_active_media ?? 0}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card><CardContent><Typography variant="overline">Submissions</Typography><Typography variant="h5">{overview?.total_submissions ?? 0}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card><CardContent><Typography variant="overline">Annotated Media</Typography><Typography variant="h5">{overview?.annotated_media_count ?? 0}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card><CardContent><Typography variant="overline">Completed Entries</Typography><Typography variant="h5">{overview?.total_completed_media_entries ?? 0}</Typography></CardContent></Card>
          </Grid>
        </Grid>

        <Card>
          <CardHeader
            title="User Summary Comparison"
            action={
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => handleDownloadSummary('csv')}>Download CSV</Button>
                <Button variant="outlined" onClick={() => handleDownloadSummary('json')}>Download JSON</Button>
              </Stack>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="ai-user-select-label">Users</InputLabel>
                <Select
                  labelId="ai-user-select-label"
                  multiple
                  value={selectedUsers}
                  label="Users"
                  onChange={(event) => setSelectedUsers(event.target.value)}
                  renderValue={(selected) => selected.length ? selected.join(', ') : 'All AI users'}
                >
                  {users.map((user) => (
                    <MenuItem key={user.username} value={user.username}>
                      {user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {summaryLoading ? (
                <Box display="flex" justifyContent="center" my={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Active Media</TableCell>
                        <TableCell>Completed Media</TableCell>
                        <TableCell>Submitted Rows</TableCell>
                        <TableCell>Submitted Media</TableCell>
                        <TableCell>Avg Quality</TableCell>
                        <TableCell>Avg Self AI</TableCell>
                        <TableCell>Avg Others AI</TableCell>
                        <TableCell>Avg Non-AI Feasibility</TableCell>
                        <TableCell>SCAW Count</TableCell>
                        <TableCell>SCAW Rate</TableCell>
                        <TableCell>Latest Submission</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparisonRows.map((row) => (
                        <TableRow key={row.username}>
                          <TableCell>{row.username}</TableCell>
                          <TableCell>{row.email || '-'}</TableCell>
                          <TableCell>{row.active_media || '-'}</TableCell>
                          <TableCell>{row.completed_media_count}</TableCell>
                          <TableCell>{row.submitted_annotation_count}</TableCell>
                          <TableCell>{row.submitted_media_count}</TableCell>
                          <TableCell>{row.avg_quality_rating ?? '-'}</TableCell>
                          <TableCell>{row.avg_self_ai_likelihood ?? '-'}</TableCell>
                          <TableCell>{row.avg_others_ai_likelihood ?? '-'}</TableCell>
                          <TableCell>{row.avg_non_ai_feasibility ?? '-'}</TableCell>
                          <TableCell>{row.contains_scaw_count}</TableCell>
                          <TableCell>{row.contains_scaw_rate ?? '-'}</TableCell>
                          <TableCell>{row.latest_submission_at ? new Date(row.latest_submission_at).toLocaleString() : '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Per-Media Comparison"
            action={
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => handleDownloadRaw('csv')} disabled={!selectedMedia}>Raw CSV</Button>
                <Button variant="outlined" onClick={() => handleDownloadRaw('json')} disabled={!selectedMedia}>Raw JSON</Button>
              </Stack>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Search media filename"
                  value={mediaSearch}
                  onChange={(event) => setMediaSearch(event.target.value)}
                />
                <Button variant="contained" onClick={handleSearchMedia} disabled={mediaLoading}>
                  Search
                </Button>
              </Stack>

              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Media Filename</TableCell>
                      <TableCell>Submissions</TableCell>
                      <TableCell>User Count</TableCell>
                      <TableCell>Latest Submission</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mediaResults.map((item) => (
                      <TableRow key={item.media_filename} selected={selectedMedia === item.media_filename}>
                        <TableCell>{item.media_filename}</TableCell>
                        <TableCell>{item.submission_count}</TableCell>
                        <TableCell>{item.user_count}</TableCell>
                        <TableCell>{item.latest_submission_at ? new Date(item.latest_submission_at).toLocaleString() : '-'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => handleLoadMediaComparison(item.media_filename)}>
                            Compare
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {mediaLoading ? (
                <Box display="flex" justifyContent="center" my={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : mediaComparison ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Comparison for {mediaComparison.media_filename}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Submission count: {mediaComparison.submission_count} | Compared users: {mediaComparison.compared_user_count}
                  </Typography>

                  <Card sx={{ mb: 2 }}>
                    <CardHeader
                      title="Media Preview"
                      action={
                        previewInfo?.url ? (
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="outlined"
                              size="small"
                              href={mediaUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              component="a"
                              href={mediaUrl}
                              download
                            >
                              Download
                            </Button>
                          </Stack>
                        ) : null
                      }
                    />
                    <CardContent>
                      {!selectedMedia ? (
                        <Typography variant="body2" color="text.secondary">
                          Select a media item to preview it here.
                        </Typography>
                      ) : !previewInfo?.url ? (
                        <Alert severity="warning">
                          No matching stored media file was found for this AI media item.
                        </Alert>
                      ) : mediaPreviewError ? (
                        <Alert severity="warning">
                          Media preview is not available for this file, but you can still try opening or downloading it.
                        </Alert>
                      ) : mediaKind === 'image' ? (
                        <Box
                          component="img"
                          src={mediaUrl}
                          alt={selectedMedia}
                          onError={() => setMediaPreviewError(true)}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 420,
                            borderRadius: 1,
                            display: 'block',
                            mx: 'auto',
                          }}
                        />
                      ) : mediaKind === 'video' ? (
                        <Box
                          component="video"
                          controls
                          src={mediaUrl}
                          onError={() => setMediaPreviewError(true)}
                          sx={{
                            width: '100%',
                            maxHeight: 420,
                            borderRadius: 1,
                            backgroundColor: '#000',
                          }}
                        />
                      ) : mediaKind === 'audio' ? (
                        <Box
                          component="audio"
                          controls
                          src={mediaUrl}
                          onError={() => setMediaPreviewError(true)}
                          sx={{ width: '100%' }}
                        />
                      ) : (
                        <Alert severity="info">
                          Inline preview is not supported for this file type. Use Open or Download.
                        </Alert>
                      )}
                    </CardContent>
                  </Card>

                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          {mediaComparison.records.map((record) => (
                            <TableCell key={record.username}>{record.username}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mediaComparisonFields.map((field) => (
                          <TableRow key={field}>
                            <TableCell>{field}</TableCell>
                            {mediaComparison.records.map((record) => (
                              <TableCell key={`${field}-${record.username}`}>
                                {formatValue(mediaComparisonMap[record.username]?.[field])}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Search for a media item and load a comparison to review user submissions side by side.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
